import { FlatTreeControl } from '@angular/cdk/tree';
import { inject, Injectable, signal } from '@angular/core';
import { GuidelineService } from 'shared/domain';
import { MAX_CHAPTER_LEVELS } from 'shared/util-common';

import { FlatTreeNode, NewGuidelineChapter, TreeNode } from '@treetop/shared/model';

import { HitboxService } from './hitbox.service';
import { Instruction } from './types';
import { areInstructionsEqual } from './utils';

@Injectable()
export class TreeDragService<T extends TreeNode<unknown>> {
  private hitboxService = inject(HitboxService);
  private guidelineService = inject(GuidelineService);

  instruction = signal<Instruction | null>(null, { equal: areInstructionsEqual });
  dragNode = signal<FlatTreeNode<T> | null>(null);
  dragNodeExpandOverNode = signal<FlatTreeNode<T> | null>(null);
  dropAllowed = signal<boolean>(true);
  dragCounter = signal<number>(0);

  public onDragStart(node: FlatTreeNode<T>): void {
    this.dragNode.set(node);
  }

  public onDragEnter(): void {
    this.dragCounter.update((counter) => counter + 1);
  }

  public onDragLeave(): void {
    this.dragCounter.update((counter) => counter - 1);
    if (this.dragCounter() === 0) {
      this.dragNodeExpandOverNode.set(null);
    }
  }

  public onDragEnd(): void {
    this.dragNode.set(null);
    this.dragNodeExpandOverNode.set(null);
    this.instruction.set(null);
    this.dropAllowed.set(true);
  }

  public onDragOver(event: DragEvent, node: FlatTreeNode<T>, containingArray: NewGuidelineChapter[]): { dropAllowed: boolean } {
    const isLastChild = containingArray[containingArray.length - 1] === node.node;

    const instruction = this.hitboxService.getInstruction({
      client: { x: event.clientX, y: event.clientY },
      borderBox: (event.currentTarget as HTMLElement).getBoundingClientRect(),
      currentLevel: node.level,
      mode: isLastChild ? 'last-in-group' : node.node.children.length > 0 ? 'expanded' : 'standard',
    });

    this.instruction.set(instruction);
    this.dragNodeExpandOverNode.set(node);

    const isAllowed = this.isDropAllowed(node);
    this.dropAllowed.set(isAllowed);

    return {
      dropAllowed: isAllowed,
    };
  }

  public onDrop(
    node: FlatTreeNode<T>,
    chapters: NewGuidelineChapter[],
    treeControl: FlatTreeControl<FlatTreeNode<T>>,
    completeArray: NewGuidelineChapter[],
  ): void {
    if (!this.isDropAllowed(node)) return;

    const latestInstruction = this.instruction();
    const targetIndex = chapters.map((chapter) => chapter.uniqueId).indexOf(node.node.id);
    const dragIndex = chapters.map((chapter) => chapter.uniqueId).indexOf(this.dragNode().node.id);
    const sameParent = this.dragNode().node.parentId === node.node.parentId;

    let newParentChapterId = node.node.parentId;
    let newIndex: number;

    let levelDifferenceResult: { newParentChapterId: string; newIndex: number } | null = null;
    switch (latestInstruction.type) {
      case 'reorder-above':
        if (sameParent) {
          newIndex = dragIndex >= targetIndex ? targetIndex : targetIndex - 1;
        } else {
          newIndex = targetIndex;
        }
        break;
      case 'reorder-below':
        if (sameParent) {
          newIndex = dragIndex > targetIndex ? targetIndex + 1 : targetIndex;
        } else {
          newIndex = targetIndex + 1;
        }
        break;
      case 'make-child':
        newParentChapterId = node.node.id;
        newIndex = node.node.children.length;
        break;
      case 'reparent':
        levelDifferenceResult = this.findLevelDifference(node, completeArray, latestInstruction, treeControl);
        newParentChapterId = levelDifferenceResult.newParentChapterId;
        newIndex = levelDifferenceResult.newIndex;
        if (this.dragNode().node.parentId === newParentChapterId) {
          newIndex = dragIndex > newIndex ? newIndex + 1 : newIndex;
        } else {
          newIndex = newIndex + 1;
        }
        break;
      default:
        break;
    }

    this.guidelineService.moveChapter(this.dragNode().node.id, newParentChapterId, newIndex);
  }

  private findLevelDifference(
    node: FlatTreeNode<T>,
    chapters: NewGuidelineChapter[],
    instruction: Exclude<Instruction, Exclude<Instruction, { type: 'reparent' }>>,
    treeControl: FlatTreeControl<FlatTreeNode<T>>,
  ): { newParentChapterId: string; newIndex: number } {
    const levelDifference = instruction.currentLevel - instruction.desiredLevel;
    let parent: FlatTreeNode<T> | null = node;
    for (let i = 0; i < levelDifference; i++) {
      parent = this.getParent(parent, treeControl);
    }
    const ar = this.getParent(parent, treeControl)?.node.children || chapters;

    return {
      newParentChapterId: parent.node.parentId,
      newIndex: ar.findIndex((chapter) => chapter.id === parent.node.id),
    };
  }

  private isDropAllowed(targetNode: FlatTreeNode<T>): boolean {
    if (this.instruction().type === 'instruction-blocked') {
      return false;
    }

    if (!this.dragNode() || (targetNode === this.dragNode() && this.instruction().type === 'make-child')) {
      this.instruction.set(this.hitboxService.applyInstructionBlock(this.instruction()));
      return false;
    }

    const desiredLevel = this.getDesiredLevel(this.instruction());
    if (desiredLevel + this.countLevelsBelowNode(this.dragNode()) > MAX_CHAPTER_LEVELS) {
      this.instruction.set(this.hitboxService.applyInstructionBlock(this.instruction()));
      return false;
    }

    return true;
  }

  private getParent(node: FlatTreeNode<T>, treeControl: FlatTreeControl<FlatTreeNode<T>>): FlatTreeNode<T> | null {
    const currentLevel = treeControl.getLevel(node);

    if (currentLevel < 1) {
      return null;
    }

    const startIndex = treeControl.dataNodes.indexOf(node) - 1;

    for (let i = startIndex; i >= 0; i--) {
      const currentNode = treeControl.dataNodes[i];

      if (treeControl.getLevel(currentNode) < currentLevel) {
        return currentNode;
      }
    }
  }

  private getDesiredLevel(instruction: Instruction): number {
    let desiredLevel: number;
    switch (instruction.type) {
      case 'reorder-above':
      case 'reorder-below':
        desiredLevel = instruction.currentLevel;
        break;
      case 'make-child':
        desiredLevel = instruction.currentLevel + 1;
        break;
      case 'reparent':
        desiredLevel = instruction.desiredLevel;
        break;
      default:
        desiredLevel = 0;
        break;
    }
    return desiredLevel;
  }

  countLevelsBelowNode(node: FlatTreeNode<T>): number {
    const countLevels = (chapter: NewGuidelineChapter, currentLevel: number): number => {
      if (!chapter.children || chapter.children.length === 0) {
        return currentLevel;
      }
      return Math.max(...chapter.children.map((child) => countLevels(child, currentLevel + 1)));
    };

    return countLevels(node.node, 1);
  }
}
